// SILNT: size the first-screen hero to whatever sits above it (announcement bar, non-transparent header).
(() => {
  const setHeroOffset = () => {
    const hero = document.querySelector('.silnt-hero--full');
    if (!hero) return;
    const offset = Math.max(0, Math.round(hero.getBoundingClientRect().top + window.scrollY));
    document.documentElement.style.setProperty('--silnt-hero-offset', `${offset}px`);
  };

  setHeroOffset();
  window.addEventListener('load', setHeroOffset);
  document.addEventListener('shopify:section:load', setHeroOffset);
})();

// SILNT: hero slideshow (sections/silnt-hero.liquid). Each slide stays for data-speed seconds while the line
// in the card fills, then the next one fades in. It holds while the card is hovered, while the lines have
// keyboard focus, off screen or in a hidden tab; choosing a slide (a line, a swipe, the arrow keys) shows it and
// carries on from there, and it never moves for reduced motion.
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const init = (root = document) => {
    root.querySelectorAll('[data-silnt-slideshow]').forEach((hero) => {
      if (hero.silntSlideshow) return;

      const slides = [...hero.querySelectorAll('[data-silnt-slide]')];
      if (slides.length < 2) return;

      const speed = (Number(hero.dataset.speed) || 7) * 1000;
      const holds = new Set();
      let current = 0;
      let stopped = reduceMotion.matches;
      let timer = null;
      let startedAt = 0;
      let remaining = speed;

      // The timer keeps the time left when it holds, just as the line's animation pauses where it is
      const pauseTimer = () => {
        if (!timer) return;
        clearTimeout(timer);
        timer = null;
        remaining -= performance.now() - startedAt;
      };

      const update = () => {
        const playing = !stopped && holds.size === 0;
        hero.classList.toggle('is-playing', !stopped);
        hero.classList.toggle('is-paused', !stopped && !playing);
        pauseTimer();
        if (!playing) return;
        startedAt = performance.now();
        timer = setTimeout(() => show(current + 1), Math.max(0, remaining));
      };

      const show = (index, { moveFocus = false } = {}) => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const active = i === current;
          slide.classList.toggle('is-active', active);
          slide.inert = !active;
        });
        if (moveFocus) slides[current].querySelector('.silnt-hero__dot.is-current')?.focus({ preventScroll: true });
        clearTimeout(timer);
        timer = null;
        remaining = speed;
        update();
      };

      const stop = () => {
        stopped = true;
        update();
      };

      const hold = (reason, on) => {
        if (on === holds.has(reason)) return;
        if (on) holds.add(reason);
        else holds.delete(reason);
        update();
      };

      hero.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-silnt-slide-to]');
        if (!dot) return;
        show(Number(dot.dataset.silntSlideTo), { moveFocus: dot === document.activeElement });
      });

      hero.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step || !event.target.closest('.silnt-hero__dots')) return;
        event.preventDefault();
        show(current + step, { moveFocus: true });
      });

      let touchX = null;
      let touchY = null;
      hero.addEventListener('touchstart', (event) => {
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
      }, { passive: true });
      hero.addEventListener('touchend', (event) => {
        if (touchX === null) return;
        const dx = event.changedTouches[0].clientX - touchX;
        const dy = event.changedTouches[0].clientY - touchY;
        touchX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        show(current + (dx < 0 ? 1 : -1));
      }, { passive: true });

      // The photo fills the first screen, so only the card holds the slide: resting the mouse there to read pauses
      // it, and a mouse click on a line or the link does not leave it paused behind the focus it gives
      hero.addEventListener('pointerover', (event) => {
        if (event.pointerType === 'mouse') hold('hover', Boolean(event.target.closest('.silnt-hero__content')));
      });
      hero.addEventListener('pointerleave', () => hold('hover', false));
      hero.addEventListener('focusin', (event) => event.target.matches(':focus-visible') && hold('focus', true));
      hero.addEventListener('focusout', (event) => !hero.contains(event.relatedTarget) && hold('focus', false));
      document.addEventListener('visibilitychange', () => hold('hidden', document.hidden));
      // Back from another page out of the browser's cache, the pointer and the focus are no longer where they were
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        hold('hover', false);
        hold('focus', false);
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => hold('offscreen', !entry.isIntersecting)).observe(hero);
      }

      hero.silntSlideshow = { slides, show, stop };
      if (document.hidden) holds.add('hidden');
      update();
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // Theme editor: selecting a slide block shows it and stops the slideshow
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-silnt-slide]');
    const slideshow = slide?.closest('[data-silnt-slideshow]')?.silntSlideshow;
    if (!slideshow) return;
    slideshow.stop();
    slideshow.show(slideshow.slides.indexOf(slide));
  });
})();

// SILNT: reveal editorial elements softly as they enter the viewport.
(() => {
  const selector = '[data-silnt-reveal]:not(.is-revealed)';
  const reveal = (el) => el.classList.add('is-revealed');

  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll(selector).forEach(reveal);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        reveal(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  const observeAll = (root = document) => root.querySelectorAll(selector).forEach((el) => observer.observe(el));

  observeAll();

  // Sections added or re-rendered in the theme editor
  document.addEventListener('shopify:section:load', (event) => observeAll(event.target));
})();

// SILNT: accessible tabs (product details). Click or use the arrow keys to move between tabs.
(() => {
  const init = (root = document) => {
    root.querySelectorAll('[data-silnt-tabs]').forEach((container) => {
      if (container.dataset.silntTabsReady) return;
      container.dataset.silntTabsReady = 'true';

      const tabs = [...container.querySelectorAll('[role="tab"]')];
      const select = (tab) => {
        tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', selected);
          t.tabIndex = selected ? 0 : -1;
          const panel = container.querySelector(`#${t.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !selected;
        });
      };

      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (!step) return;
          event.preventDefault();
          const next = tabs[(i + step + tabs.length) % tabs.length];
          select(next);
          next.focus();
        });
      });
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

// SILNT: "SILENT" settles into "SILNT" only once the whole word is in view (About page).
(() => {
  const selector = '[data-silnt-settle]:not(.is-settled)';
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll(selector).forEach((el) => el.classList.add('is-settled'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-settled');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.9 });

  const observeAll = (root = document) => root.querySelectorAll(selector).forEach((el) => observer.observe(el));
  observeAll();
  document.addEventListener('shopify:section:load', (event) => observeAll(event.target));
})();

// SILNT: "You may also like" in the product page's right column.
// Loads Shopify's recommendations for this product, then drives the arrows and progress bar.
if (!customElements.get('silnt-recommendations')) {
  class SilntRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      this.loaded = true;
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const track = doc.querySelector('[data-silnt-recs-track]');
          if (!track || !track.children.length) return;
          this.querySelector('[data-silnt-recs-body]').appendChild(track);
          this.hidden = false;
          this.initCarousel(track);
        })
        .catch(() => {});
    }

    initCarousel(track) {
      const prev = this.querySelector('[data-silnt-recs-prev]');
      const next = this.querySelector('[data-silnt-recs-next]');
      const bar = this.querySelector('[data-silnt-recs-bar]');
      const step = () => (track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth);

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const visible = track.clientWidth / track.scrollWidth;
        bar.style.width = `${Math.min(100, visible * 100)}%`;
        bar.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * ((1 - visible) / visible) * 100 : 0}%)`;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= max - 1;
        this.classList.toggle('silnt-recs--static', max <= 1);
        const media = track.querySelector('.silnt-recs__media');
        if (media) this.style.setProperty('--silnt-recs-media-height', `${media.getBoundingClientRect().height}px`);
      };

      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }
  }

  customElements.define('silnt-recommendations', SilntRecommendations);
}

// SILNT: links like "/#the-quiet-list" scroll to that part of the current page when it exists here
// (the newsletter band lives in the footer of every page).
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href^="/#"]');
  if (!link) return;
  const target = document.getElementById(link.getAttribute('href').slice(2));
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// SILNT: Find your ritual. Three questions -> one recommended ritual, added to the bag in one tap.
(() => {
  const init = (root = document) => {
    root.querySelectorAll('[data-silnt-quiz]').forEach((quiz) => {
      if (quiz.dataset.quizReady) return;
      quiz.dataset.quizReady = 'true';

      const intro = quiz.querySelector('[data-quiz-intro]');
      const form = quiz.querySelector('[data-quiz-form]');
      const steps = [...quiz.querySelectorAll('[data-quiz-step]')];
      const results = quiz.querySelector('[data-quiz-results]');
      const count = quiz.querySelector('[data-quiz-count]');
      const bar = quiz.querySelector('[data-quiz-bar]');
      const back = quiz.querySelector('[data-quiz-back]');
      let current = 0;

      const show = (index) => {
        current = index;
        steps.forEach((step, i) => { step.hidden = i !== index; });
        count.textContent = `${index + 1} / ${steps.length}`;
        bar.style.width = `${((index + 1) / steps.length) * 100}%`;
        back.style.visibility = index === 0 ? 'hidden' : 'visible';
        const first = steps[index].querySelector('input:checked') || steps[index].querySelector('input');
        if (first) first.focus({ preventScroll: true });
      };

      const resultKey = (answers) => {
        const { skin, concern, time } = answers;
        if (concern === 'firming' && time === 'three' && skin === 'sensitive') return 'firming-three-sensitive';
        if (concern === 'brightening' && time === 'three' && skin === 'dry') return 'brightening-three-dry';
        return `${concern}-${time}`;
      };

      const finish = () => {
        const answers = Object.fromEntries(new FormData(form));
        const key = resultKey(answers);
        form.hidden = true;
        results.hidden = false;
        results.querySelectorAll('[data-quiz-result]').forEach((r) => { r.hidden = r.dataset.quizResult !== key; });
        quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      quiz.querySelector('[data-quiz-start]').addEventListener('click', () => {
        intro.hidden = true;
        form.hidden = false;
        show(0);
      });

      form.addEventListener('change', (event) => {
        if (event.target.type !== 'radio') return;
        // A short pause lets the chosen answer register visually before moving on
        setTimeout(() => (current < steps.length - 1 ? show(current + 1) : finish()), 350);
      });

      back.addEventListener('click', () => current > 0 && show(current - 1));

      quiz.querySelectorAll('[data-quiz-restart]').forEach((btn) => btn.addEventListener('click', () => {
        form.reset();
        results.hidden = true;
        intro.hidden = true;
        form.hidden = false;
        show(0);
      }));

      quiz.querySelectorAll('[data-quiz-add]').forEach((btn) => btn.addEventListener('click', async () => {
        const ids = btn.dataset.quizAdd.split(',').filter(Boolean);
        if (!ids.length) return;
        btn.classList.add('is-loading');
        btn.setAttribute('aria-disabled', 'true');
        // routes.root keeps the visitor in their language (/es/cart rather than /cart)
        const root = window.Shopify?.routes?.root || '/';
        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) })
          });
          if (!response.ok) throw new Error(response.status);
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          const drawer = document.querySelector('.js-cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            setTimeout(() => drawer.open(btn), 250);
          } else {
            window.location.href = `${root}cart`;
          }
        } catch (error) {
          window.location.href = `${root}cart`;
        } finally {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-disabled');
        }
      }));
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

// SILNT: country & language panel (snippets/silnt-localization.liquid).
// Triggers can be cloned (the theme copies the announcement bar into the mobile menu), so everything is delegated.
(() => {
  const panel = () => document.querySelector('[data-silnt-locale]');
  let lastTrigger = null;

  const open = (trigger) => {
    const el = panel();
    if (!el) return;
    lastTrigger = trigger;
    el.hidden = false;
    document.documentElement.classList.add('silnt-locale-open');
    requestAnimationFrame(() => {
      el.classList.add('is-open');
      el.querySelector('.silnt-locale__panel').focus({ preventScroll: true });
    });
  };

  const close = () => {
    const el = panel();
    if (!el || el.hidden) return;
    el.classList.remove('is-open');
    document.documentElement.classList.remove('silnt-locale-open');
    setTimeout(() => { el.hidden = true; }, 300);
    if (lastTrigger) lastTrigger.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-silnt-locale-open]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }

    if (event.target.closest('[data-silnt-locale-close]')) {
      close();
      return;
    }

    const toggle = event.target.closest('.silnt-locale__toggle');
    if (toggle) {
      const list = document.getElementById(toggle.getAttribute('aria-controls'));
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      if (list) list.hidden = expanded;
      return;
    }

    const choice = event.target.closest('.silnt-locale__choose');
    if (choice) {
      const form = choice.closest('form');
      form.querySelector('[name="country_code"]').value = choice.dataset.country;
      form.querySelector('[name="language_code"]').value = choice.dataset.language;
      form.submit();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
})();

// SILNT: one-tap add from "Complete the ritual" (snippets/silnt-ritual.liquid); opens the bag like the quiz does.
document.addEventListener('click', async (event) => {
  const btn = event.target.closest('[data-silnt-quick-add]');
  if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
  const root = window.Shopify?.routes?.root || '/';
  const label = btn.textContent;
  btn.setAttribute('aria-disabled', 'true');
  btn.classList.add('is-loading');
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.dataset.silntQuickAdd), quantity: 1 }] })
    });
    if (!response.ok) throw new Error(response.status);
    btn.textContent = btn.dataset.addedLabel || label;
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    const drawer = document.querySelector('.js-cart-drawer');
    if (btn.closest('.js-cart-drawer')) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    } else if (drawer && typeof drawer.open === 'function') {
      setTimeout(() => drawer.open(btn), 250);
    } else {
      window.location.href = `${root}cart`;
    }
    setTimeout(() => { btn.textContent = label; }, 2500);
  } catch (error) {
    window.location.href = `${root}cart`;
  } finally {
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-disabled');
  }
});

// SILNT: "Save with the set" in the bag (snippets/silnt-bag-suggest.liquid). Adds the set first, then takes one unit
// of each product it replaces out of the bag (data-swap is "line key|quantity left," per product).
document.addEventListener('click', async (event) => {
  const btn = event.target.closest('[data-silnt-set-swap]');
  if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
  const root = window.Shopify?.routes?.root || '/';
  const post = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(response.status);
  };
  btn.setAttribute('aria-disabled', 'true');
  btn.classList.add('is-loading');
  try {
    await post('cart/add.js', { items: [{ id: Number(btn.dataset.silntSetSwap), quantity: 1 }] });
    for (const entry of btn.dataset.swap.split(',').filter(Boolean)) {
      const [key, quantity] = entry.split('|');
      await post('cart/change.js', { id: key, quantity: Number(quantity) });
    }
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    if (btn.closest('.js-cart-drawer')) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    } else {
      window.location.reload();
    }
  } catch (error) {
    window.location.href = `${root}cart`;
  } finally {
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-disabled');
  }
});
